import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyClaim, submitClaim } from '../services/membershipService';
import { getDirectoryTenants } from '../services/tenantService';

const CLAIM_STATUS_LABELS = {
  unverified:          'Unverified',
  self_declared:       'Self-Declared Member',
  community_pending:   'Pending Community Review',
  community_verified:  'Verified Community Member',
  revoked:             'Revoked',
};

const MembershipPage = () => {
  const { activeTenant, activeMembership, memberships } = useAuth();
  const navigate = useNavigate();

  // Current-tenant claim state
  const [claim, setClaim]       = useState(null);
  const [claimLoading, setClaimLoading] = useState(true);
  const [submitting, setSub]    = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [form, setForm]         = useState({ communityName: '', membershipNumber: '', notes: '' });

  // Directory state
  const [directory, setDirectory]     = useState([]);
  const [dirLoading, setDirLoading]   = useState(true);
  const [slugInput, setSlugInput]     = useState('');

  useEffect(() => {
    if (activeTenant?._id) {
      getMyClaim(activeTenant._id)
        .then((res) => setClaim(res.data))
        .catch(() => setClaim(null))
        .finally(() => setClaimLoading(false));
    } else {
      setClaimLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenant?._id]);

  useEffect(() => {
    getDirectoryTenants()
      .then((res) => setDirectory(res.data))
      .catch(() => setDirectory([]))
      .finally(() => setDirLoading(false));
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmitClaim = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSub(true);
    try {
      const res = await submitClaim({
        tenantId:       activeTenant?._id,
        declaredStatus: 'self_declared',
        supportingInfo: form,
      });
      setClaim(res.data);
      setSuccess('Membership claim submitted successfully.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit claim');
    } finally {
      setSub(false);
    }
  };

  const handleSlugGo = (e) => {
    e.preventDefault();
    const slug = slugInput.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-');
    if (slug) navigate(`/t/${slug}/join`);
  };

  const activeMemberships = (memberships || []).filter(m => m.status === 'active');

  return (
    <div style={{ padding: '28px 32px', maxWidth: 860, margin: '0 auto', color: '#e2e8f0' }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', marginBottom: 4 }}>Membership</h2>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 28 }}>
        You participate as <strong style={{ color: '#e2e8f0' }}>General Public</strong> by default on all projects.
        Join a community below to submit reports and participate under your community identity.
      </p>

      {/* ── Active memberships ─────────────────────────────────────────────── */}
      {activeMemberships.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', marginBottom: 12 }}>
            Your Community Memberships
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
            {activeMemberships.map(m => {
              const t     = m.tenant;
              const name  = t?.branding?.displayName || t?.name || 'Community';
              const color = t?.branding?.primaryColor || '#3b82f6';
              return (
                <div key={m._id} style={{
                  background: '#1e293b', border: `1px solid ${color}44`,
                  borderLeft: `3px solid ${color}`, borderRadius: 8, padding: '12px 14px',
                }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#f1f5f9' }}>{name}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
                    Role: {m.communityRole} &nbsp;&middot;&nbsp; {m.status}
                  </div>
                  {t?.slug && (
                    <Link to={`/t/${t.slug}`} style={{
                      display: 'inline-block', marginTop: 8, fontSize: 11, color: '#7dd3fc', textDecoration: 'none',
                    }}>
                      Open community &rarr;
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Current-tenant claim status / form ────────────────────────────── */}
      {activeTenant && (
        <div style={{
          background: '#1e293b', border: '1px solid #334155', borderRadius: 10,
          padding: '18px 20px', marginBottom: 28,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', marginBottom: 10 }}>
            {activeTenant.branding?.displayName || activeTenant.name} — Membership Status
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{
              background: activeMembership?.status === 'active' ? '#14532d' : '#1e293b',
              border: `1px solid ${activeMembership?.status === 'active' ? '#22c55e' : '#64748b'}`,
              color: activeMembership?.status === 'active' ? '#86efac' : '#94a3b8',
              borderRadius: 5, padding: '3px 10px', fontSize: 12, fontWeight: 600,
            }}>
              {activeMembership?.status || 'No membership'}
            </span>
          </div>

          {claimLoading ? (
            <p style={{ color: '#64748b', fontSize: 13 }}>Loading claim&hellip;</p>
          ) : claim ? (
            <div style={{ fontSize: 13, color: '#94a3b8' }}>
              <p style={{ margin: '0 0 4px' }}>
                Claim status: <strong style={{ color: '#e2e8f0' }}>{CLAIM_STATUS_LABELS[claim.resolvedStatus] || claim.resolvedStatus}</strong>
              </p>
              {claim.reviewNotes && <p style={{ margin: '0 0 4px' }}>Notes: {claim.reviewNotes}</p>}
              {claim.reviewedAt  && <p style={{ margin: 0 }}>Reviewed: {new Date(claim.reviewedAt).toLocaleDateString()}</p>}
            </div>
          ) : (
            <form onSubmit={handleSubmitClaim} style={{ display: 'grid', gap: 10, maxWidth: 500 }}>
              <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>
                Submit a membership claim to be verified as a community member.
              </p>
              {error   && <div style={{ color: '#f87171', fontSize: 12 }}>{error}</div>}
              {success && <div style={{ color: '#86efac', fontSize: 12 }}>{success}</div>}
              <input name="communityName" placeholder="Community Name" value={form.communityName}
                onChange={handleChange} style={{
                  background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
                  color: '#e2e8f0', padding: '8px 11px', fontSize: 13,
                }} />
              <input name="membershipNumber" placeholder="Membership Number (optional)" value={form.membershipNumber}
                onChange={handleChange} style={{
                  background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
                  color: '#e2e8f0', padding: '8px 11px', fontSize: 13,
                }} />
              <textarea name="notes" placeholder="Additional notes (optional)" value={form.notes}
                onChange={handleChange} rows={2} style={{
                  background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
                  color: '#e2e8f0', padding: '8px 11px', fontSize: 13, resize: 'vertical',
                }} />
              <div>
                <button type="submit" disabled={submitting} style={{
                  background: '#1d4ed8', border: 'none', borderRadius: 6,
                  color: '#fff', padding: '7px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}>
                  {submitting ? 'Submitting\u2026' : 'Submit Claim'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* ── Find & Join Communities ────────────────────────────────────────── */}
      <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 }}>
        Find a Community
      </div>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
        Browse communities listed below, or enter a community code shared with you directly.
      </p>

      {/* Slug entry */}
      <form onSubmit={handleSlugGo} style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          value={slugInput}
          onChange={e => setSlugInput(e.target.value)}
          placeholder="Enter community slug or code (e.g. my-community)"
          style={{
            flex: '1 1 260px', background: '#1e293b', border: '1px solid #334155',
            borderRadius: 6, color: '#e2e8f0', padding: '8px 12px', fontSize: 13,
          }}
        />
        <button type="submit" disabled={!slugInput.trim()} style={{
          background: '#1e3a5f', border: '1px solid #3b82f6', borderRadius: 6,
          color: '#7dd3fc', padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
        }}>
          Go to Join Page &rarr;
        </button>
      </form>

      {/* Directory listing */}
      {dirLoading ? (
        <p style={{ color: '#64748b', fontSize: 13 }}>Loading directory&hellip;</p>
      ) : directory.length === 0 ? (
        <div style={{
          background: '#1e293b', border: '1px dashed #334155', borderRadius: 10,
          padding: '24px 20px', textAlign: 'center', color: '#475569', fontSize: 13,
        }}>
          No communities are currently listed in the public directory.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
          {directory.map(t => {
            const name  = t.branding?.displayName || t.name;
            const color = t.branding?.primaryColor || '#3b82f6';
            return (
              <div key={t._id} style={{
                background: '#1e293b', border: `1px solid ${color}33`,
                borderTop: `3px solid ${color}`, borderRadius: 10, padding: '16px 18px',
                display: 'flex', flexDirection: 'column', gap: 8,
              }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#f1f5f9' }}>{name}</div>
                {t.description && (
                  <div style={{
                    fontSize: 12, color: '#94a3b8',
                    overflow: 'hidden', display: '-webkit-box',
                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }}>{t.description}</div>
                )}
                <div style={{ fontSize: 11, color: '#64748b' }}>
                  {t.type?.replace('_', ' ')}
                </div>
                <Link to={`/t/${t.slug}/join`} style={{
                  marginTop: 'auto', display: 'inline-block', textAlign: 'center',
                  background: '#0f172a', border: `1px solid ${color}`, borderRadius: 6,
                  color, padding: '6px 14px', fontSize: 12, fontWeight: 600, textDecoration: 'none',
                }}>
                  Join Community
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MembershipPage;
